import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import kestioLogoWhite from "../assets/kestio-logo-white.png";
import { authApi } from "../lib/api";
import { useTranslation } from "react-i18next";

interface AuthVerifyPageProps {
  token: string;
  onLogin: (user: any) => void;
  onBack: () => void;
}

export function AuthVerifyPage({ token, onLogin, onBack }: AuthVerifyPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      try {
        const user = await authApi.verifyMagicLink(token);
        // Le verify ne renvoie que la ligne `users` (sanitizeUser) : il manque
        // les champs portés uniquement par /api/auth/me (accountOnboardingCompletedAt,
        // accountConfigured, companyName, companyLogoUrl, avatarUrl…). On réhydrate
        // via /me — le cookie httpOnly est déjà posé par le verify — pour que la
        // garde d'onboarding (App.tsx) dispose des bons flags. Sans ça, un ORG déjà
        // onboardé revoit le tunnel à CHAQUE connexion par magic link (flags undefined).
        // Même patron que AuthPage.handleTestLogin. Repli défensif sur l'objet du
        // verify si /me est momentanément indisponible (la connexion reste valide).
        let fullUser = user;
        try {
          fullUser = await authApi.me();
        } catch {
          /* /me indisponible : on garde l'objet du verify, /me se recalera au reload */
        }
        if (!cancelled) {
          setStatus("success");
          // Small delay to show success, then redirect
          setTimeout(() => {
            if (!cancelled) onLogin(fullUser);
          }, 1000);
        }
      } catch (err: any) {
        if (!cancelled) {
          setStatus("error");
          setError(err.message || t("auth.invalidOrExpired"));
        }
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-end justify-center gap-3 mb-4 pb-1">
            <img src={kestioLogoWhite} alt="Kestio" className="h-8 invert" />
            <span
              className="font-bold text-2xl relative"
              style={{ fontFamily: 'Sora, sans-serif', color: '#384872', top: '8px' }}
            >
              BizCoach
            </span>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Sora, sans-serif' }}>
            {status === "loading" && t("auth.verifying")}
            {status === "success" && t("auth.loginSuccess")}
            {status === "error" && t("auth.invalidLink")}
          </CardTitle>
          <CardDescription>
            {status === "loading" && t("auth.verifyingLink")}
            {status === "success" && t("auth.redirecting")}
            {status === "error" && t("auth.linkNoLongerValid")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            {status === "loading" && (
              <div className="flex justify-center">
                <Loader2 className="h-16 w-16 text-primary animate-spin" />
              </div>
            )}

            {status === "success" && (
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
            )}

            {status === "error" && (
              <>
                <div className="flex justify-center">
                  <XCircle className="h-16 w-16 text-red-500" />
                </div>
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button className="w-full" onClick={onBack}>
                  {t("auth.backToLogin")}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
